import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Theme, teamsLightTheme, teamsDarkTheme } from '@fluentui/react-components';
import { useAuth } from './AuthContext';
import { teamsAuth } from '../lib/teams-auth';
import { lightTheme, darkTheme } from '../theme';
import { readStoredValue, writeStoredValue } from '../lib/safeStorage';

const THEME_MODE_STORAGE_KEY = 'quickdial-theme-mode';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type ThemeSource = 'system' | 'teams' | 'custom';

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  effectiveMode: 'light' | 'dark'; // The actual mode being used (auto resolved to light/dark)
  themeSource: ThemeSource;
  setThemeMode: (mode: ThemeMode) => void;
  isTeamsContext: boolean;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Detects system/browser theme preference
 */
function detectSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

/**
 * Detects Teams theme (if running in Teams)
 */
async function detectTeamsTheme(): Promise<'light' | 'dark'> {
  try {
    return await teamsAuth.getTheme();
  } catch (error) {
    console.warn('Error detecting Teams theme:', error);
  }
  
  // Fallback to system theme
  return detectSystemTheme();
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { isInTeams: authIsInTeams } = useAuth();
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    // Load saved preference from storage. Must go through safeStorage: a raw
    // localStorage read throws in the iOS host webview (see safeStorage.ts) and
    // a throw in this initializer takes down the whole tree before the M365
    // loading indicator is ever dismissed.
    const saved = readStoredValue(THEME_MODE_STORAGE_KEY);
    return (saved as ThemeMode) || 'auto';
  });
  
  const [effectiveMode, setEffectiveMode] = useState<'light' | 'dark'>('light');
  const [themeSource, setThemeSource] = useState<ThemeSource>('system');
  const isTeamsContext = !!authIsInTeams;

  // Determine the actual theme to use
  const theme: Theme = React.useMemo(() => {
    if (isTeamsContext) {
      return effectiveMode === 'dark' ? teamsDarkTheme : teamsLightTheme;
    }
    return effectiveMode === 'dark' ? darkTheme : lightTheme;
  }, [effectiveMode, isTeamsContext]);

  // Update effective mode based on themeMode setting
  useEffect(() => {
    const updateEffectiveMode = async () => {
      if (themeMode === 'light') {
        setEffectiveMode('light');
        setThemeSource('custom');
      } else if (themeMode === 'dark') {
        setEffectiveMode('dark');
        setThemeSource('custom');
      } else {
        // Auto mode - detect from Teams or system
        if (isTeamsContext) {
          const teamsTheme = await detectTeamsTheme();
          setEffectiveMode(teamsTheme);
          setThemeSource('teams');
        } else {
          const systemTheme = detectSystemTheme();
          setEffectiveMode(systemTheme);
          setThemeSource('system');
        }
      }
    };

    updateEffectiveMode();
  }, [themeMode, isTeamsContext]);

  // Listen for system theme changes (only in auto mode)
  useEffect(() => {
    if (themeMode !== 'auto' || isTeamsContext) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setEffectiveMode(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode, isTeamsContext]);

  // Listen for Teams theme changes (only in auto mode)
  useEffect(() => {
    if (themeMode !== 'auto' || !isTeamsContext) return;

    try {
      teamsAuth.registerOnThemeChangeHandler((theme) => {
        console.log('Teams theme changed:', theme);
        setEffectiveMode(theme);
      });
    } catch (error) {
      console.warn('Error registering Teams theme change handler:', error);
    }
  }, [themeMode, isTeamsContext]);

  // Save theme mode preference
  useEffect(() => {
    writeStoredValue(THEME_MODE_STORAGE_KEY, themeMode);
  }, [themeMode]);

  // Update document class for global styles
  useEffect(() => {
    document.documentElement.classList.remove('light-theme', 'dark-theme');
    document.documentElement.classList.add(`${effectiveMode}-theme`);
    
    // Also set data attribute for easier CSS targeting
    document.documentElement.setAttribute('data-theme', effectiveMode);
  }, [effectiveMode]);

  const value: ThemeContextType = {
    theme,
    themeMode,
    effectiveMode,
    themeSource,
    setThemeMode: (mode: ThemeMode) => {
      setThemeMode(mode);
      console.log('Theme mode changed to:', mode);
    },
    isTeamsContext,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to check if dark mode is active
 */
export function useIsDarkMode(): boolean {
  const { effectiveMode } = useTheme();
  return effectiveMode === 'dark';
}

/**
 * Hook to toggle between light and dark mode
 */
export function useToggleTheme(): () => void {
  const { themeMode, setThemeMode } = useTheme();
  
  return () => {
    if (themeMode === 'auto') {
      // If auto, switch to explicit dark
      setThemeMode('dark');
    } else if (themeMode === 'light') {
      setThemeMode('dark');
    } else {
      setThemeMode('light');
    }
  };
}
